import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Building2, Mail, Phone, MapPin, Hash,
  FileText, AlertTriangle, CheckCircle2, Download,
  Package, ShoppingCart, Edit2, ChevronRight,
} from 'lucide-react';
import {
  fournisseurs, equipements, commandes,
  formatDate, formatMontant,
} from '../data/mockData';

const typeColors = {
  Materiel: 'info',
  Prestation: 'warn',
  Licence: 'sky',
};

const FournisseurDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fournisseur = fournisseurs.find((f) => f.id === id);

  if (!fournisseur) {
    return (
      <div className="page-container">
        <button className="back-link" onClick={() => navigate('/fournisseurs')}>
          <ArrowLeft size={16} /> Retour aux fournisseurs
        </button>
        <div className="empty-state">
          <Building2 size={28} />
          <p>Fournisseur introuvable.</p>
        </div>
      </div>
    );
  }

  const catalogue = equipements.filter((eq) =>
    eq.references.some((r) => r.fournisseurId === fournisseur.id)
  );
  const commandesFournisseur = commandes.filter((c) => c.fournisseurId === fournisseur.id);
  const alertes = fournisseur.documents.filter((d) => d.statut === 'a_renouveler').length;

  return (
    <div className="page-container">
      <button className="back-link" onClick={() => navigate('/fournisseurs')}>
        <ArrowLeft size={16} /> Retour aux fournisseurs
      </button>

      <div className="page-header">
        <div>
          <div className="page-title-row">
            <div className="page-icon-wrapper">
              <Building2 size={22} />
            </div>
            <div>
              <h1 className="page-title">{fournisseur.nom}</h1>
              <p className="page-subtitle mono">{fournisseur.id} - SIRET {fournisseur.siret}</p>
            </div>
          </div>
        </div>
        <div className="page-actions">
          {alertes > 0 ? (
            <span className="badge badge-warn">
              <AlertTriangle size={12} /> {alertes} document(s) a renouveler
            </span>
          ) : (
            <span className="badge badge-ok">
              <CheckCircle2 size={12} /> Dossier conforme
            </span>
          )}
          <button className="btn-ghost"><Edit2 size={14} /> Modifier</button>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card">
          <div className="kpi-icon"><Package size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Catalogue produit</span>
            <span className="kpi-value">{fournisseur.nbProduits}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><ShoppingCart size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Commandes traitees</span>
            <span className="kpi-value">{fournisseur.nbCommandes}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><FileText size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Documents legaux</span>
            <span className="kpi-value">{fournisseur.documents.length}</span>
          </div>
        </div>
        <div className="kpi-card kpi-amber">
          <div className="kpi-icon"><AlertTriangle size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">A renouveler</span>
            <span className="kpi-value">{alertes}</span>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <Building2 size={18} />
            <h2>Fiche fournisseur</h2>
          </div>
        </header>
        <div className="card-body info-grid">
          <div>
            <span className="info-label">Contact principal</span>
            <span className="info-value">{fournisseur.contact}</span>
          </div>
          <div>
            <span className="info-label"><Mail size={12} /> Email</span>
            <span className="info-value">{fournisseur.email}</span>
          </div>
          <div>
            <span className="info-label"><Phone size={12} /> Telephone</span>
            <span className="info-value">{fournisseur.telephone}</span>
          </div>
          <div>
            <span className="info-label"><Hash size={12} /> SIRET</span>
            <span className="info-value mono">{fournisseur.siret}</span>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span className="info-label"><MapPin size={12} /> Adresse</span>
            <span className="info-value">{fournisseur.adresse}</span>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <FileText size={18} />
            <h2>Documents legaux</h2>
          </div>
          <button className="btn-ghost btn-small">
            <FileText size={14} /> Ajouter un document
          </button>
        </header>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Date de validite</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {fournisseur.documents.map((doc) => (
                <tr key={doc.type}>
                  <td><strong>{doc.type}</strong></td>
                  <td>{formatDate(doc.dateValidite)}</td>
                  <td>
                    <span className={`badge badge-${doc.statut === 'a_renouveler' ? 'warn' : 'ok'}`}>
                      {doc.statut === 'a_renouveler' ? (
                        <><AlertTriangle size={12} /> A renouveler</>
                      ) : (
                        <><CheckCircle2 size={12} /> Conforme</>
                      )}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn-ghost btn-small">
                      <Download size={14} /> Telecharger
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <Package size={18} />
            <h2>Catalogue produit ({catalogue.length})</h2>
          </div>
        </header>
        <div className="card-body">
          {catalogue.length === 0 ? (
            <div className="empty-state compact">
              <p>Aucun produit reference pour ce fournisseur.</p>
            </div>
          ) : (
            <table className="data-table clickable">
              <thead>
                <tr>
                  <th>Equipement</th>
                  <th>Type</th>
                  <th>Reference fournisseur</th>
                  <th>Delai</th>
                  <th style={{ textAlign: 'right' }}>Prix</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {catalogue.map((eq) => {
                  const ref = eq.references.find((r) => r.fournisseurId === fournisseur.id);
                  return (
                    <tr key={eq.id} className="hoverable clickable" onClick={() => navigate(`/equipements/${eq.id}`)}>
                      <td>
                        <div className="cell-strong">
                          <Package size={14} className="cell-icon" />
                          <div>
                            <p>{eq.libelle}</p>
                            <span className="meta-line mono">{eq.marque} / {eq.modele}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${typeColors[eq.type]}`}>{eq.type}</span>
                      </td>
                      <td><span className="mono">{ref?.reference}</span></td>
                      <td>{ref?.delai}</td>
                      <td style={{ textAlign: 'right' }}><strong>{formatMontant(ref?.prixAchat)}</strong></td>
                      <td style={{ width: 28 }}><ChevronRight size={16} className="arrow-muted" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <ShoppingCart size={18} />
            <h2>Historique commandes ({commandesFournisseur.length})</h2>
          </div>
        </header>
        <div className="card-body">
          {commandesFournisseur.length === 0 ? (
            <div className="empty-state compact">
              <p>Aucune commande pour ce fournisseur.</p>
            </div>
          ) : (
            <table className="data-table clickable">
              <thead>
                <tr>
                  <th>Commande</th>
                  <th>Date</th>
                  <th>Chantier</th>
                  <th style={{ textAlign: 'right' }}>Montant HT</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {commandesFournisseur.map((cmd) => (
                  <tr key={cmd.id} className="hoverable clickable" onClick={() => navigate(`/commandes/${cmd.id}`)}>
                    <td><span className="mono">{cmd.id}</span></td>
                    <td>{formatDate(cmd.date)}</td>
                    <td>{cmd.chantier}</td>
                    <td style={{ textAlign: 'right' }}><strong>{formatMontant(cmd.montantHT)}</strong></td>
                    <td style={{ width: 28 }}><ChevronRight size={16} className="arrow-muted" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default FournisseurDetail;
